import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import "../css/vehicle.css";
import SuperAdminNav from "../components/adNav";
import Time from "../components/time";
import Swal from "sweetalert2";
import { useNavigate, Link } from "react-router-dom";

// ⬇️ helper to read profile once
function getAdminFromStorage() {
  try {
    const raw = localStorage.getItem("nissi_admin");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

const AddMember_Admin = () => {
  const navigate = useNavigate();

  // ⬇️ read admin profile (has num_users & num_licenses)
  const admin = useMemo(() => getAdminFromStorage(), []);
  const allowedUsers = useMemo(() => Number(admin?.num_users || 0), [admin]);

 // Module visibility derived from num_licenses
const numLic = useMemo(() => Number(admin?.num_licenses || 0), [admin]);
const showVehicle   = numLic === 1 || numLic === 3; // 1 or 3 → vehicle visible
const showInventory = numLic === 2 || numLic === 3; // 2 or 3 → inventory visible
const bothHidden    = !showVehicle && !showInventory;

// NEW: collapse visibility into a single number for the API
const moduleCode = useMemo(() => {
  if (showVehicle && showInventory) return 3; // both
  if (showVehicle) return 1;                  // vehicle
  if (showInventory) return 2;                // inventory
  return 0;                                   // none (fallback)
}, [showVehicle, showInventory]);

  // ⬇️ gate before rendering the form (user limit check)
  const [gateChecked, setGateChecked] = useState(false);

  useEffect(() => {
    // not logged in? bounce to login
    if (!admin) {
      navigate("/login", { replace: true });
      return;
    }

    // unlimited plan? allow immediately
    if (!allowedUsers || allowedUsers <= 0) {
      setGateChecked(true);
      return;
    }

    // otherwise check count from backend
    (async () => {
      try {
        const res = await axios.get("https://rfs2.infraconsoft.net.in/api/admin_list");
        const currentCount = Array.isArray(res.data?.ListData) ? res.data.ListData.length : 0;

        if (currentCount >= allowedUsers) {
          // show alert on THIS PAGE, then redirect
          await Swal.fire({
            icon: "info",
            title: "User limit reached",
            text: "Please contact Nissi Infotech staff to increase your user capacity.",
          });
          navigate("/admin/admin_members_list", { replace: true });
          return;
        }

        setGateChecked(true); // ok to show form
      } catch (err) {
        console.error("Precheck failed:", err);
        await Swal.fire({
          icon: "error",
          title: "Unable to verify capacity",
          text: "Please try again.",
        });
        navigate("/admin/admin_members_list", { replace: true });
      }
    })();
  }, [admin, allowedUsers, navigate]);

  // while checking, render a small placeholder
  // 
  // ===== existing state & logic =====
  const [selected1, setSelected1] = useState([]); // Inventory - Master
  const [selected2, setSelected2] = useState([]); // Inventory - Transaction
  const [selected3, setSelected3] = useState([]); // Inventory - Report
  const [selected4, setSelected4] = useState([]); // Vehicle - Transaction
  const [selected5, setSelected5] = useState([]); // Vehicle - Master
  const [selected6, setSelected6] = useState([]); // Vehicle - Report

  const userId = localStorage.getItem("username");
  const [formData, setFormData] = useState({
    memberId: "", userName: "", name: "", password: "", confirmPassword: "",
    branch: "", desc: "", cat: "",
  });

  const [branches, setBranches] = useState([]);
  const [dept, setDept] = useState([]);
  const [options, setOptions] = useState([]);   // Inventory - Master
  const [options1, setOptions1] = useState([]); // Inventory - Transaction
  const [options2, setOptions2] = useState([]); // Inventory - Report
  const [options3, setOptions3] = useState([]); // Vehicle - Transaction
  const [options4, setOptions4] = useState([]); // Vehicle - Master
  const [options5, setOptions5] = useState([]); // Vehicle - Report

  const [dropdown1Visible, setDropdown1Visible] = useState(false);
  const [dropdown2Visible, setDropdown2Visible] = useState(false);
  const [dropdown3Visible, setDropdown3Visible] = useState(false);
  const [dropdown4Visible, setDropdown4Visible] = useState(false);
  const [dropdown5Visible, setDropdown5Visible] = useState(false);
  const [dropdown6Visible, setDropdown6Visible] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const fetchAllInitialData = async () => {
      let progress = 0;
      let interval;

      Swal.fire({
        title: "Loading Admin Setup",
        html: `
          <div id="progress-container" style="width: 100%; background: #e0e0e0; border-radius: 5px;">
            <div id="progress-bar" style="height: 10px; width: 0%; background: linear-gradient(to right, #4facfe, #00f2fe); border-radius: 5px;"></div>
          </div>
          <p id="progress-text" style="margin-top: 10px; font-size: 14px;">Loading... 0%</p>
        `,
        showConfirmButton: false,
        allowOutsideClick: false,
        didOpen: () => {
          const bar = document.getElementById("progress-bar");
          const text = document.getElementById("progress-text");

          interval = setInterval(() => {
            progress += 5;
            if (progress > 95) progress = 95;
            if (bar) bar.style.width = `${progress}%`;
            if (text) text.innerText = `Loading... ${progress}%`;
          }, 100);
        },
      });

      try {
        const [branchRes, deptRes, optionsRes] = await Promise.all([
          axios.get(`https://rfs2.infraconsoft.net.in/api/admin_branches`),
          axios.get(`https://rfs2.infraconsoft.net.in/api/admin_categories`),
          axios.get(`https://rfs2.infraconsoft.net.in/api/addAdmin_inventory1`)
        ]);

        // Set Branches
        setBranches(branchRes.data.branchList || []);

        // Set Departments
        setDept(deptRes.data.catList || []);

        // Set Inventory & Vehicle Options
        const opts = optionsRes.data || {};
        setOptions(opts.addListInventory1 || []); // Inv - Master
        setOptions1(opts.addListInventory2 || []); // Inv - Trans
        setOptions2(opts.addListInventory3 || []); // Inv - Report
        setOptions3(opts.addListInventory4 || []); // Veh - Trans
        setOptions4(opts.addListInventory5 || []); // Veh - Master
        setOptions5(opts.addListInventory6 || []); // Veh - Report

        // Complete progress safely
        progress = 100;
        const bar = document.getElementById("progress-bar");
        const text = document.getElementById("progress-text");

        if (bar) bar.style.width = "100%";
        if (text) text.innerText = "Loading... 100%";

        setTimeout(() => Swal.close(), 500);
      } finally {
        clearInterval(interval);
      }
    };

    fetchAllInitialData();
  }, []);

  // Debug the option arrays (optional)
  useEffect(() => {
    // console.log("Options1 (Inv-Master):", options);
    // console.log("Options2 (Inv-Trans):", options1);
  }, [options, options1, options2, options3, options4, options5]);

  const toggleCheckbox = (index, setSelectedFunction, selectedArray) => {
    if (selectedArray.includes(index)) {
      setSelectedFunction(selectedArray.filter(item => item !== index));
    } else {
      setSelectedFunction([...selectedArray, index]);
    }
  };

  const handleSelectAll = (list, setSelectedFunction, selectedArray) => {
    if (selectedArray.length === list.length) {
      setSelectedFunction([]);
    } else {
      setSelectedFunction(list.map((_, index) => index));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.memberId.trim()) {
      Swal.fire({ icon: 'warning', title: 'Empty Field', text: 'Please Enter Your Member Id!' });
      return;
    }
    if (!formData.userName.trim()) {
      Swal.fire({ icon: 'warning', title: 'Empty Field', text: 'Please Enter Your Username!' });
      return;
    }
    if (!formData.name.trim()) {
      Swal.fire({ icon: 'warning', title: 'Empty Field', text: 'Please Enter Your Name!' });
      return;
    }
    if (!formData.desc.trim()) {
      Swal.fire({ icon: 'warning', title: 'Empty Field', text: 'Please Enter Your Designation!' });
      return;
    }
    if (!formData.password.trim()) {
      Swal.fire({ icon: 'warning', title: 'Empty Field', text: 'Please Enter Your Password!' });
      return;
    }
    if (formData.password.trim().length < 6) {
      Swal.fire({ icon: 'warning', title: 'Weak Password', text: 'Password must be at least 6 characters!' });
      return;
    }
    if (!formData.confirmPassword.trim()) {
      Swal.fire({ icon: 'warning', title: 'Empty Field', text: 'Please Confirm Your Password!' });
      return;
    }
    if (formData.password.trim() !== formData.confirmPassword.trim()) {
      Swal.fire({ icon: 'error', title: 'Password Mismatch', text: 'Password and Confirm Password do not match!' });
      return;
    }
    if (!formData.branch.trim()) {
      Swal.fire({ icon: 'warning', title: 'Empty Field', text: 'Please Select your Branch!' });
      return;
    }
    if (!formData.cat.trim()) {
      Swal.fire({ icon: 'warning', title: 'Empty Field', text: 'Please Enter Your Category!' });
      return;
    }

    try {
      // Build selected item titles ONLY from visible modules
      const invMaster   = showInventory ? selected1.map(i => options[i]?.title || '') : [];
      const invTrans    = showInventory ? selected2.map(i => options1[i]?.title || '') : [];
      const invReport   = showInventory ? selected3.map(i => options2[i]?.title || '') : [];
      const vehTrans    = showVehicle   ? selected4.map(i => options3[i]?.title || '') : [];
      const vehMaster   = showVehicle   ? selected5.map(i => options4[i]?.title || '') : [];
      const vehReport   = showVehicle   ? selected6.map(i => options5[i]?.title || '') : [];

      const selectedItems = [
        ...invMaster, ...invTrans, ...invReport,
        ...vehMaster, ...vehTrans, ...vehReport,
      ].filter(item => item.trim() !== "");

      if (selectedItems.length === 0) {
        Swal.fire("Error", "Please add at least one Menu", "error");
        return;
      }

      const payload = {
        user: userId, 
        memberId: formData.memberId,
        userName: formData.userName,
        name: formData.name,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        branch: formData.branch,
        desc: formData.desc,
        cat: formData.cat,
        selectedItems,
         dept: moduleCode,   
      };

      Swal.fire({
        title: "Submitting...",
        text: "Please wait while we process your request.",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const response = await axios.post("https://rfs2.infraconsoft.net.in/api/admin_add", payload);

      if (response.data.success) {
        await Swal.fire({ icon: "success", title: "Success", text: "Member added successfully!" });
        navigate('/admin/admin_members_list');
      } else {
        Swal.fire({ icon: "error", title: "Error", text: response.data.message || "Submission failed. Please try again." });
      }

    } catch (error) {
      console.error("Error submitting form:", error);
      let errorMessage = "Submission failed. Please try again.";
      if (error.response?.data?.message) errorMessage = error.response.data.message;
      Swal.fire({ icon: "error", title: "Error", text: errorMessage });
    }
  };

  // helpers for grid layout (single column when only one module visible)
  const gridStyleFor = (colsVisible) => ({
    display: "grid",
    gridTemplateColumns: colsVisible === 2 ? "1fr 1fr" : "1fr",
    gap: 20,
    marginTop: 20,
  });

  if (!gateChecked) {
    return (
      <div id="admin-container">
        <SuperAdminNav />
        <div className="content-right">
          <section>
            <h2 className="add-txt">Admin Members - Add</h2>
            <Time />
          </section>
          <div className="form-container" style={{ padding: 24, opacity: 0.6 }}>
            Checking capacity…
          </div>
        </div>
      </div>
    );
  }


  return (
    <div id="admin-container">
      <SuperAdminNav />
      <div className="content-right">
        <section>
          <div className="back-div">
            <h2 className="add-txt">Admin Members - Add</h2>
            <Link to={`/admin/admin_members_list`}>
              <p className="back-button"><i className="fa-solid fa-backward"></i>Back</p>
            </Link>
          </div>
          <Time />
        </section>

        <div className="form-container">
          {/* Warn if plan has no modules */}
          {bothHidden && (
            <div style={{ padding: 12, background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 8, marginBottom: 16 }}>
              No modules available for your plan. Please contact Nissi Infotech staff.
            </div>
          )}

          <form className="vehicle-form" onSubmit={handleSubmit}>
            {/* Basic details */}
            <div className="form-row">
              <div className="form-group">
                <label>Member Id :</label>
                <input type="text" name="memberId" value={formData.memberId} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>User Name:</label>
                <input type="text" name="userName" value={formData.userName} onChange={handleChange} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Name :</label>
                <input type="text" name="name" value={formData.name} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Password:</label>
                <input type="password" name="password" value={formData.password} onChange={handleChange} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Designation :</label>
                <input type="text" name="desc" value={formData.desc} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Confirm Password:</label>
                <input type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Branch :</label>
                <select name="branch" value={formData.branch} onChange={handleChange}>
                  <option value="">-- Select Branch --</option>
                  {branches.map((item, index) => (
                    <option key={index} value={item.branchMaster_code}>
                      {item.branchMaster_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Select Category :</label>
                <select name="cat" value={formData.cat} onChange={handleChange}>
                  <option value="">-- Select Category --</option>
                  {dept.map((item, index) => (
                    <option key={index} value={item.fld_category}>
                      {item.fld_category}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* ===== MASTER MODULES ===== */}
            {!bothHidden && (
              <div className="dropdown-container" style={gridStyleFor((showInventory ? 1 : 0) + (showVehicle ? 1 : 0))}>
                {/* Inventory Master */}
                {showInventory && (
                  <div className="dropdown">
                    <button type="button" onClick={() => setDropdown1Visible(!dropdown1Visible)}>
                      Master Module - Inventory {dropdown1Visible ? "▲" : "▼"}
                    </button>
                    {dropdown1Visible && (
                      <div className="admin-checkbox-container">
                        <label className="admin-checkbox-label">
                          <input
                            type="checkbox"
                            className="admin-checkbox-input"
                            checked={options.length > 0 && selected1.length === options.length}
                            onChange={() => handleSelectAll(options, setSelected1, selected1)}
                          />
                          Select All
                        </label>
                        {options.map((option, index) => (
                          <label key={index} className="admin-checkbox-label">
                            <input
                              type="checkbox"
                              className="admin-checkbox-input"
                              checked={selected1.includes(index)}
                              onChange={() => toggleCheckbox(index, setSelected1, selected1)}
                            />
                            {option.title || option.name || JSON.stringify(option)}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Vehicle Master */}
                {showVehicle && (
                  <div className="dropdown">
                    <button type="button" onClick={() => setDropdown5Visible(!dropdown5Visible)}>
                      Master Module - Vehicle {dropdown5Visible ? "▲" : "▼"}
                    </button>
                    {dropdown5Visible && (
                      <div className="admin-checkbox-container">
                        <label className="admin-checkbox-label">
                          <input
                            type="checkbox"
                            className="admin-checkbox-input"
                            checked={options4.length > 0 && selected5.length === options4.length}
                            onChange={() => handleSelectAll(options4, setSelected5, selected5)}
                          />
                          Select All
                        </label>
                        {options4.map((option, index) => (
                          <label key={index} className="admin-checkbox-label">
                            <input
                              type="checkbox"
                              className="admin-checkbox-input"
                              checked={selected5.includes(index)}
                              onChange={() => toggleCheckbox(index, setSelected5, selected5)}
                            />
                            {option.title || option.name || JSON.stringify(option)}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ===== TRANSACTION MODULES ===== */}
            {!bothHidden && (
              <div className="dropdown-container" style={gridStyleFor((showInventory ? 1 : 0) + (showVehicle ? 1 : 0))}>
                {/* Inventory Transaction */}
                {showInventory && (
                  <div className="dropdown">
                    <button type="button" onClick={() => setDropdown2Visible(!dropdown2Visible)}>
                      Transcation Module - Inventory {dropdown2Visible ? "▲" : "▼"}
                    </button>
                    {dropdown2Visible && (
                      <div className="admin-checkbox-container">
                        <label className="admin-checkbox-label">
                          <input
                            type="checkbox"
                            className="admin-checkbox-input"
                            checked={options1.length > 0 && selected2.length === options1.length}
                            onChange={() => handleSelectAll(options1, setSelected2, selected2)}
                          />
                          Select All
                        </label>
                        {options1.map((option, index) => (
                          <label key={index} className="admin-checkbox-label">
                            <input
                              type="checkbox"
                              className="admin-checkbox-input"
                              checked={selected2.includes(index)}
                              onChange={() => toggleCheckbox(index, setSelected2, selected2)}
                            />
                            {option.title || option.name || JSON.stringify(option)}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Vehicle Transaction */}
                {showVehicle && (
                  <div className="dropdown">
                    <button type="button" onClick={() => setDropdown4Visible(!dropdown4Visible)}>
                      Transcation Module - Vehicle {dropdown4Visible ? "▲" : "▼"}
                    </button>
                    {dropdown4Visible && (
                      <div className="admin-checkbox-container">
                        <label className="admin-checkbox-label">
                          <input
                            type="checkbox"
                            className="admin-checkbox-input"
                            checked={options3.length > 0 && selected4.length === options3.length}
                            onChange={() => handleSelectAll(options3, setSelected4, selected4)}
                          />
                          Select All
                        </label>
                        {options3.map((option, index) => (
                          <label key={index} className="admin-checkbox-label">
                            <input
                              type="checkbox"
                              className="admin-checkbox-input"
                              checked={selected4.includes(index)}
                              onChange={() => toggleCheckbox(index, setSelected4, selected4)}
                            />
                            {option.title || option.name || JSON.stringify(option)}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ===== REPORT MODULES ===== */}
            {!bothHidden && (
              <div className="dropdown-container" style={gridStyleFor((showInventory ? 1 : 0) + (showVehicle ? 1 : 0))}>
                {/* Inventory Report */}
                {showInventory && (
                  <div className="dropdown">
                    <button type="button" onClick={() => setDropdown3Visible(!dropdown3Visible)}>
                      Report Module - Inventory {dropdown3Visible ? "▲" : "▼"}
                    </button>
                    {dropdown3Visible && (
                      <div className="admin-checkbox-container">
                        <label className="admin-checkbox-label">
                          <input
                            type="checkbox"
                            className="admin-checkbox-input"
                            checked={options2.length > 0 && selected3.length === options2.length}
                            onChange={() => handleSelectAll(options2, setSelected3, selected3)}
                          />
                          Select All
                        </label>
                        {options2.map((option, index) => (
                          <label key={index} className="admin-checkbox-label">
                            <input
                              type="checkbox"
                              className="admin-checkbox-input"
                              checked={selected3.includes(index)}
                              onChange={() => toggleCheckbox(index, setSelected3, selected3)}
                            />
                            {option.title || option.name || JSON.stringify(option)}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Vehicle Report */}
                {showVehicle && (
                  <div className="dropdown">
                    <button type="button" onClick={() => setDropdown6Visible(!dropdown6Visible)}>
                      Report Module - Vehicle {dropdown6Visible ? "▲" : "▼"}
                    </button>
                    {dropdown6Visible && (
                      <div className="admin-checkbox-container">
                        <label className="admin-checkbox-label">
                          <input
                            type="checkbox"
                            className="admin-checkbox-input"
                            checked={options5.length > 0 && selected6.length === options5.length}
                            onChange={() => handleSelectAll(options5, setSelected6, selected6)}
                          />
                          Select All
                        </label>
                        {options5.map((option, index) => (
                          <label key={index} className="admin-checkbox-label">
                            <input
                              type="checkbox"
                              className="admin-checkbox-input"
                              checked={selected6.includes(index)}
                              onChange={() => toggleCheckbox(index, setSelected6, selected6)}
                            />
                            {option.title || option.name || JSON.stringify(option)}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="btn-cls" style={{ marginTop: 16 }}>
              <button type="submit" className="submit-btn" disabled={bothHidden}>
                Checkout
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddMember_Admin;
